import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { getCurrentUser, isAuthenticated } from "../utils/auth";
import CartPlaceService from "../Redux/Services/CartPlaceService";

// Helper function to extract normalized numeric price from cart item
const getNumericPrice = (item) => {
  if (!item) return 0;
  const price = item.selectedProductPrice ?? item.price ?? item.finalPrice ?? item.unitPrice ?? 0;
  const num = Number(price);
  return isNaN(num) ? 0 : num;
};

// Helper function to extract size identifier string
const getSizeKey = (selectedSize) => {
  if (!selectedSize) return "standard";
  if (typeof selectedSize === "object" && selectedSize.packageSize) {
    return String(selectedSize.packageSize).trim().toLowerCase();
  }
  return String(selectedSize).trim().toLowerCase();
};

// Helper function to calculate total amount and total quantity accurately
const calculateTotals = (cartItems = []) => {
  return cartItems.reduce(
    (acc, item) => {
      const price = getNumericPrice(item);
      const qty = Number(item.cartQuantity) || 1;
      acc.totalAmount += price * qty;
      acc.totalQuantity += qty;
      return acc;
    },
    { totalAmount: 0, totalQuantity: 0 }
  );
};

// Helper function to safely load and normalize cart items from localStorage
const loadCartFromStorage = () => {
  try {
    const rawCart = localStorage.getItem("cart");
    if (!rawCart) return [];
    const parsed = JSON.parse(rawCart);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((item) => {
      const numericPrice = getNumericPrice(item);
      const sizeKey = getSizeKey(item.selectedSize);
      const cartItemId = item.cartItemId || `${item.id}_${sizeKey}`;
      return {
        ...item,
        cartItemId,
        selectedProductPrice: numericPrice,
        price: numericPrice,
        cartQuantity: Math.max(1, Number(item.cartQuantity) || 1),
      };
    });
  } catch (err) {
    console.error("Error reading cart from localStorage:", err);
    return [];
  }
};

const initialCartItems = isAuthenticated() ? loadCartFromStorage() : [];
const initialTotals = calculateTotals(initialCartItems);

const initialState = {
  cartState: false,
  cartItems: initialCartItems,
  cartTotalAmount: initialTotals.totalAmount,
  cartTotalQantity: initialTotals.totalQuantity,
};

const CartSlice = createSlice({
  initialState,
  name: "cart",
  reducers: {
    setOpenCart: (state, action) => {
      state.cartState = action.payload?.cartState ?? true;
    },
    setCloseCart: (state, action) => {
      state.cartState = action.payload?.cartState ?? false;
    },

    setAddItemToCart: (state, action) => {
      const payload = action.payload;
      if (!payload) return;

      const { id, name } = payload;
      const numericPrice = getNumericPrice(payload);
      const sizeKey = getSizeKey(payload.selectedSize);
      const cartItemId = payload.cartItemId || `${id}_${sizeKey}`;

      const existingIndex = state.cartItems.findIndex(
        (item) => item.cartItemId === cartItemId || (item.id === id && getSizeKey(item.selectedSize) === sizeKey)
      );

      if (existingIndex >= 0) {
        state.cartItems[existingIndex].cartQuantity += 1;
        state.cartItems[existingIndex].selectedProductPrice = numericPrice;
        state.cartItems[existingIndex].price = numericPrice;
        toast.success(`Updated ${name || "Item"} quantity in Cart`);
      } else {
        const newItem = {
          ...payload,
          cartItemId,
          selectedProductPrice: numericPrice,
          price: numericPrice,
          cartQuantity: 1,
        };
        state.cartItems.push(newItem);
        toast.success(`${name || "Item"} added to Cart`);
      }

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },

    setRemoveItemFromCart: (state, action) => {
      const target = action.payload;
      const targetId = typeof target === "object" && target !== null ? (target.cartItemId || target.id) : target;
      const targetSizeKey = typeof target === "object" && target !== null ? getSizeKey(target.selectedSize) : null;

      const itemsAfterRemoval = state.cartItems.filter((item) => {
        if (item.cartItemId && item.cartItemId === targetId) return false;
        if (targetSizeKey && item.id === targetId && getSizeKey(item.selectedSize) === targetSizeKey) return false;
        return item.id !== targetId;
      });

      state.cartItems = itemsAfterRemoval;

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));

      const itemName = typeof target === "object" && target?.name ? target.name : "Item";
      toast.success(`${itemName} Removed From Cart`);
    },

    setIncreaseItemQTY: (state, action) => {
      const target = action.payload;
      const targetId = typeof target === "object" && target !== null ? (target.cartItemId || target.id) : target;
      const targetSizeKey = typeof target === "object" && target !== null ? getSizeKey(target.selectedSize) : null;

      const itemIndex = state.cartItems.findIndex((item) => {
        if (item.cartItemId && item.cartItemId === targetId) return true;
        if (targetSizeKey && item.id === targetId && getSizeKey(item.selectedSize) === targetSizeKey) return true;
        return item.id === targetId;
      });

      if (itemIndex >= 0) {
        state.cartItems[itemIndex].cartQuantity += 1;
        toast.success("Item QTY Increased");
      }

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },

    setDecreaseItemQTY: (state, action) => {
      const target = action.payload;
      const targetId = typeof target === "object" && target !== null ? (target.cartItemId || target.id) : target;
      const targetSizeKey = typeof target === "object" && target !== null ? getSizeKey(target.selectedSize) : null;

      const itemIndex = state.cartItems.findIndex((item) => {
        if (item.cartItemId && item.cartItemId === targetId) return true;
        if (targetSizeKey && item.id === targetId && getSizeKey(item.selectedSize) === targetSizeKey) return true;
        return item.id === targetId;
      });

      if (itemIndex >= 0) {
        if (state.cartItems[itemIndex].cartQuantity > 1) {
          state.cartItems[itemIndex].cartQuantity -= 1;
          toast.success("Item QTY Decreased");
        }
      }

      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;

      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },

    setClearCartItems: (state) => {
      state.cartItems = [];
      state.cartTotalAmount = 0;
      state.cartTotalQantity = 0;

      localStorage.setItem("cart", JSON.stringify([]));
      localStorage.setItem("totalAmount", JSON.stringify(0));
      toast.success("Cart Cleared");
    },

    setGetTotals: (state) => {
      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;
      localStorage.setItem("totalAmount", JSON.stringify(totalAmount));
    },

    setSyncCartItems: (state, action) => {
      state.cartItems = action.payload;
      const { totalAmount, totalQuantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = totalAmount;
      state.cartTotalQantity = totalQuantity;
      localStorage.setItem("cart", JSON.stringify(state.cartItems));
      localStorage.setItem("totalAmount", JSON.stringify(state.cartTotalAmount));
    },
  },
});

export const {
  setOpenCart,
  setCloseCart,
  setAddItemToCart,
  setRemoveItemFromCart,
  setIncreaseItemQTY,
  setDecreaseItemQTY,
  setClearCartItems,
  setGetTotals,
  setSyncCartItems,
} = CartSlice.actions;

export const selectCartState = (state) => state.cart.cartState;
export const selectCartItems = (state) => state.cart.cartItems;
export const selectTotalAmount = (state) => state.cart.cartTotalAmount;
export const selectTotalQTY = (state) => state.cart.cartTotalQantity;

// --- Async Thunks for Backend Integration ---

export const addToCartDb = (item) => async (dispatch, getState) => {
  dispatch(setAddItemToCart(item));

  const user = getCurrentUser();
  if (user) {
    const userId = user.userId || user.userCode || localStorage.getItem("userCode") || "CUSTOMER";
    try {
      const state = getState();
      const addedItem = state.cart.cartItems.find(
        (it) => it.cartItemId === item.cartItemId || (it.id === item.id && getSizeKey(it.selectedSize) === getSizeKey(item.selectedSize))
      );

      const payload = {
        userId: userId,
        productCode: item.productCode || item.code || String(item.id),
        productName: (item.name || item.productName || "").replace(/\s+/g, '_').toUpperCase(),
        productCategoryCode: item.productCategoryCode || item.categoryCode || "General",
        productSubCategoryCode: item.productSubCategoryCode || item.productSubcategoryCode || item.subcategoryCode || "Herbal Supplements and Medicines",
        qty: addedItem ? Number(addedItem.cartQuantity) : 1,
        unitPrice: Number(item.selectedProductPrice ?? item.price ?? 0),
        discountAmount: 0.00,
        discountPercentage: Number(item.discountPercentage || 0),
        finalPrice: Number(item.selectedProductPrice ?? item.price ?? 0),
        createdBy: user.firstName || "CUSTOMER",
        modifiedBy: user.firstName || "CUSTOMER"
      };
      await CartPlaceService.AddtoCart(payload);
      dispatch(fetchCartListDb());
    } catch (err) {
      console.error("Failed to sync add-to-cart to backend:", err);
    }
  }
};

export const increaseQtyDb = (id) => async (dispatch, getState) => {
  dispatch(setIncreaseItemQTY(id));

  const user = getCurrentUser();
  const state = getState();
  const item = state.cart.cartItems.find(it => it.cartItemId === id || it.id === id);

  if (user && item) {
    const userId = user.userId || user.userCode || localStorage.getItem("userCode") || "CUSTOMER";
    const productCode = item.productCode || item.code || String(item.id);
    const newQty = Number(item.cartQuantity);

    try {
      if (item.cartId) {
        const payload = {
          modifiedBy: userId,
          qty: newQty
        };
        await CartPlaceService.updateCartQuantity(item.cartId, productCode, payload);
      } else {
        const payload = {
          userId: userId,
          productCode: productCode,
          productName: (item.name || item.productName || "").replace(/\s+/g, '_').toUpperCase(),
          productCategoryCode: item.productCategoryCode || item.categoryCode || "General",
          productSubCategoryCode: item.productSubCategoryCode || item.productSubcategoryCode || item.subcategoryCode || "Herbal Supplements and Medicines",
          qty: newQty,
          unitPrice: Number(item.selectedProductPrice ?? item.price ?? 0),
          discountAmount: 0.00,
          discountPercentage: Number(item.discountPercentage || 0),
          finalPrice: Number(item.selectedProductPrice ?? item.price ?? 0),
          createdBy: user.firstName || "CUSTOMER",
          modifiedBy: user.firstName || "CUSTOMER"
        };
        await CartPlaceService.AddtoCart(payload);
        dispatch(fetchCartListDb());
      }
    } catch (err) {
      console.error("Failed to sync increment to backend:", err);
    }
  }
};

export const decreaseQtyDb = (item) => async (dispatch, getState) => {
  const itemId = item.cartItemId || item.id;
  dispatch(setDecreaseItemQTY(itemId));

  const user = getCurrentUser();
  const state = getState();
  const updatedItem = state.cart.cartItems.find(it => it.cartItemId === itemId || it.id === item.id);

  if (user && updatedItem) {
    const userId = user.userId || user.userCode || localStorage.getItem("userCode") || "CUSTOMER";
    const productCode = updatedItem.productCode || updatedItem.code || String(updatedItem.id);
    const newQty = Number(updatedItem.cartQuantity);

    try {
      if (updatedItem.cartId) {
        const payload = {
          modifiedBy: userId,
          qty: newQty
        };
        await CartPlaceService.updateCartQuantity(updatedItem.cartId, productCode, payload);
      } else {
        const payload = {
          userId: userId,
          productCode: productCode,
          productName: (updatedItem.name || updatedItem.productName || "").replace(/\s+/g, '_').toUpperCase(),
          productCategoryCode: updatedItem.productCategoryCode || updatedItem.categoryCode || "General",
          productSubCategoryCode: updatedItem.productSubCategoryCode || updatedItem.productSubcategoryCode || updatedItem.subcategoryCode || "Herbal Supplements and Medicines",
          qty: newQty,
          unitPrice: Number(updatedItem.selectedProductPrice ?? updatedItem.price ?? 0),
          discountAmount: 0.00,
          discountPercentage: Number(updatedItem.discountPercentage || 0),
          finalPrice: Number(updatedItem.selectedProductPrice ?? updatedItem.price ?? 0),
          createdBy: user.firstName || "CUSTOMER",
          modifiedBy: user.firstName || "CUSTOMER"
        };
        await CartPlaceService.AddtoCart(payload);
        dispatch(fetchCartListDb());
      }
    } catch (err) {
      console.error("Failed to sync decrement to backend:", err);
    }
  }
};

export const removeItemDb = (item) => async (dispatch, getState) => {
  dispatch(setRemoveItemFromCart(item));

  const user = getCurrentUser();
  if (user) {
    const userId = user.userId || user.userCode || localStorage.getItem("userCode") || "CUSTOMER";
    const productCode = item.productCode || item.code || String(item.id);

    try {
      if (item.cartId) {
        await CartPlaceService.deleteFromCart(item.cartId, productCode);
      } else {
        const payload = {
          userId: userId,
          productCode: productCode,
          productName: (item.name || item.productName || "").replace(/\s+/g, '_').toUpperCase(),
          productCategoryCode: item.productCategoryCode || item.categoryCode || "General",
          productSubCategoryCode: item.productSubCategoryCode || item.productSubcategoryCode || item.subcategoryCode || "Herbal Supplements and Medicines",
          qty: 0,
          unitPrice: Number(item.selectedProductPrice ?? item.price ?? 0),
          discountAmount: 0.00,
          discountPercentage: Number(item.discountPercentage || 0),
          finalPrice: Number(item.selectedProductPrice ?? item.price ?? 0),
          createdBy: user.firstName || "CUSTOMER",
          modifiedBy: user.firstName || "CUSTOMER"
        };
        await CartPlaceService.AddtoCart(payload);
        dispatch(fetchCartListDb());
      }
    } catch (err) {
      console.error("Failed to sync removal to backend:", err);
    }
  }
};

export const fetchCartListDb = () => async (dispatch) => {
  const user = getCurrentUser();
  if (!user) return;
  const userId = user.userId || user.userCode || localStorage.getItem("userCode");
  if (!userId) return;

  try {
    const response = await CartPlaceService.getCartList(userId);
    if (response && response.data) {
      const cartHeaders = response.data;
      let backendItems = [];

      if (Array.isArray(cartHeaders)) {
        cartHeaders.forEach((header) => {
          if (header.status === 1 && Array.isArray(header.cartDetailsList)) {
            header.cartDetailsList.forEach((detail) => {
              if (detail.status === 1) {
                const sizeVal = detail.packageSize || "Standard";
                backendItems.push({
                  id: detail.id,
                  productCode: detail.productCode,
                  name: detail.productName || (detail.productCode || "").replace(/_/g, " "),
                  price: Number(detail.payableAmount ?? detail.totalAmount ?? 0),
                  selectedProductPrice: Number(detail.payableAmount ?? detail.totalAmount ?? 0),
                  cartQuantity: Number(detail.qty || 1),
                  selectedSize: typeof sizeVal === "object" ? sizeVal : { packageSize: sizeVal },
                  cartItemId: `${detail.productCode}_${sizeVal.toString().toLowerCase()}`,
                  imageName: detail.productImage,
                  imageUrl: detail.productImage,
                  productImage: detail.productImage,
                  cartId: detail.cartId || header.cartId,
                });
              }
            });
          }
        });
      }

      if (backendItems.length > 0) {
        dispatch(setSyncCartItems(backendItems));
      }
    }
  } catch (err) {
    console.error("Failed to fetch cart list:", err);
  }
};

export default CartSlice.reducer;